/**
 * `useCostSchemeBoard` — the editable board state for the Cost Scheme agenda.
 *
 * Owns the local dirty {@link BoardModel} (seeded from the program-scheme GET response), an in-grid undo
 * stack (Ctrl+Z), and every board mutation used by the timeline, strips and drawer. The model is the
 * source of truth for the agenda's lifetime: it re-seeds from a fresh response ONLY while there are no
 * unsaved edits, so a background refetch never clobbers work in progress.
 *
 * Dirty tracking is revision-based (columbus-planner pattern): every user mutation bumps `changeToken`;
 * a save records the token it persisted into `savedToken`. `dirty` = the two diverge, so edits made while
 * a save is in flight keep the board dirty and trigger another autosave.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import type { GetProgramSchemeResponse } from '@/api/generated/schemas';
import { PlaceholderType } from '@/api/generated/schemas';
import {
  getGetProductsPlaceholderQueryKey,
  getProductsPlaceholder,
} from '@/api/generated/products-placeholders/products-placeholders';
import {
  DAY_END,
  DEFAULT_START,
  NIGHT_START,
  mapResponseToModel,
  snapQuarter,
  type BoardModel,
  type BoardPart,
  type PalettePayload,
} from '../model';
import { DEFAULT_TIMED_HOURS } from '../model';
import {
  fallbackSnapshot,
  newSpanPart,
  newTimedPart,
  placeholderToSnapshot,
  type PlaceholderSnapshot,
} from './parts';
import { nightCovered, serviceCovered } from './coverage';

const HISTORY_LIMIT = 100;

export interface CostSchemeBoard {
  model: BoardModel;
  dirty: boolean;
  /** Monotonic revision that changes on every user mutation — feeds the autosave debounce. */
  changeToken: number;
  canUndo: boolean;
  undo: () => void;
  canRedo: boolean;
  redo: () => void;
  /**
   * Records that everything up to `token` (a {@link changeToken} value captured at save start) has been
   * persisted. If newer edits landed while the save was in flight (`changeToken > token`), the board
   * stays dirty so autosave fires again — no edit is silently dropped.
   */
  markSaved: (token: number) => void;
  dropPalette: (payload: PalettePayload, dayIdx: number, startHour: number) => void;
  dropAccommodation: (payload: PalettePayload, nightIdx: number) => void;
  dropService: (payload: PalettePayload, dayIdx: number, row: number) => void;
  moveAccommodation: (uid: string, nightIdx: number) => void;
  resizeAccommodation: (uid: string, nights: number) => void;
  moveService: (uid: string, dayIdx: number, row: number) => void;
  resizeService: (uid: string, days: number) => void;
  moveTimed: (uid: string, dayShift: number, hourShift: number) => void;
  resizeTimed: (uid: string, hourDelta: number) => void;
  updatePart: (uid: string, patch: Partial<BoardPart>) => void;
  deletePart: (uid: string) => void;
}

export function useCostSchemeBoard(response: GetProgramSchemeResponse): CostSchemeBoard {
  const [model, setModel] = useState<BoardModel>(() => mapResponseToModel(response));
  const [changeToken, setChangeToken] = useState(0);
  const [savedToken, setSavedToken] = useState(0);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const queryClient = useQueryClient();

  const dirty = changeToken !== savedToken;

  const modelRef = useRef(model);
  modelRef.current = model;
  const revisionRef = useRef(0);
  const savedRef = useRef(0);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const historyRef = useRef<BoardModel[]>([]);
  const futureRef = useRef<BoardModel[]>([]);
  const seededRef = useRef(response);

  // Re-seed from a fresh response only when there are no unsaved local edits (background refetch,
  // inheritance override). A normal save keeps the model authoritative — client-generated part ids
  // mean nothing needs reconciling — so it does not invalidate the schema query and never re-seeds.
  useEffect(() => {
    if (response !== seededRef.current && !dirtyRef.current) {
      seededRef.current = response;
      const next = mapResponseToModel(response);
      modelRef.current = next;
      setModel(next);
      historyRef.current = [];
      futureRef.current = [];
      setCanUndo(false);
      setCanRedo(false);
      revisionRef.current = 0;
      savedRef.current = 0;
      setChangeToken(0);
      setSavedToken(0);
    }
  }, [response]);

  const bumpRevision = useCallback(() => {
    revisionRef.current += 1;
    setChangeToken(revisionRef.current);
  }, []);

  // Single mutation entry point: applies `updater`, pushes the previous model onto the undo stack, and
  // bumps the change token. A no-op updater (returns the same reference) is ignored so undo stays clean.
  const commit = useCallback(
    (updater: (model: BoardModel) => BoardModel) => {
      const prev = modelRef.current;
      const next = updater(prev);
      if (next === prev) return;
      historyRef.current.push(prev);
      if (historyRef.current.length > HISTORY_LIMIT) historyRef.current.shift();
      futureRef.current = []; // a fresh edit invalidates any redo path
      modelRef.current = next;
      setModel(next);
      setCanUndo(true);
      setCanRedo(false);
      bumpRevision();
    },
    [bumpRevision]
  );

  const mapParts = useCallback(
    (fn: (part: BoardPart) => BoardPart) => {
      commit((m) => ({ ...m, parts: m.parts.map(fn) }));
    },
    [commit]
  );

  const undo = useCallback(() => {
    const history = historyRef.current;
    if (history.length === 0) return;
    const prev = history.pop() as BoardModel;
    futureRef.current.push(modelRef.current); // current state becomes redoable
    modelRef.current = prev;
    setModel(prev);
    setCanUndo(history.length > 0);
    setCanRedo(true);
    bumpRevision();
  }, [bumpRevision]);

  const redo = useCallback(() => {
    const future = futureRef.current;
    if (future.length === 0) return;
    const next = future.pop() as BoardModel;
    historyRef.current.push(modelRef.current); // current state becomes undoable again
    modelRef.current = next;
    setModel(next);
    setCanUndo(true);
    setCanRedo(future.length > 0);
    bumpRevision();
  }, [bumpRevision]);

  const markSaved = useCallback((token: number) => {
    if (token > savedRef.current) {
      savedRef.current = token;
      setSavedToken(token);
    }
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      const key = event.key.toLowerCase();
      if (key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      } else if ((key === 'z' && event.shiftKey) || key === 'y') {
        event.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // Load the source placeholder's settings so a new part is a full snapshot of it (facility sub-type,
  // category, unit patterns, equipment, cost type, visibility, note, transport kind). The backend persists
  // the DTO verbatim and never re-reads the placeholder, so this copy must happen client-side at drop time.
  // Cached via React Query (same key as the drawer); on failure we fall back to a payload-only snapshot so
  // the drop still works.
  const resolveSnapshot = useCallback(
    async (payload: PalettePayload): Promise<PlaceholderSnapshot> => {
      // Blank box (no source placeholder) — nothing to fetch, snapshot carries only the payload.
      const placeholderId = payload.placeholderId;
      if (placeholderId == null) return fallbackSnapshot(payload);
      try {
        const detail = await queryClient.fetchQuery({
          queryKey: getGetProductsPlaceholderQueryKey(placeholderId),
          queryFn: ({ signal }) => getProductsPlaceholder(placeholderId, signal),
        });
        return placeholderToSnapshot(detail, payload.name);
      } catch {
        return fallbackSnapshot(payload);
      }
    },
    [queryClient]
  );

  const addAccommodation = useCallback(
    async (payload: PalettePayload, nightIdx: number) => {
      const snapshot = await resolveSnapshot(payload);
      commit((m) => {
        if (nightIdx < 0 || nightIdx >= m.lengthDays - 1) return m;
        if (nightCovered(m.parts, nightIdx)) return m;
        return {
          ...m,
          parts: [...m.parts, newSpanPart(payload, 'accommodation', nightIdx, null, snapshot)],
        };
      });
    },
    [commit, resolveSnapshot]
  );

  const dropPalette = useCallback(
    async (payload: PalettePayload, dayIdx: number, startHour: number) => {
      // Accommodation placeholders belong only in the accommodation strip, never on the hour timeline.
      if (payload.type === PlaceholderType.Accommodation) return;
      const snapshot = await resolveSnapshot(payload);
      commit((m) => {
        const clampedStart = Math.min(startHour, DAY_END - DEFAULT_TIMED_HOURS);
        return {
          ...m,
          parts: [
            ...m.parts,
            newTimedPart(payload, dayIdx, Math.max(NIGHT_START, clampedStart), snapshot),
          ],
        };
      });
    },
    [commit, resolveSnapshot]
  );

  const dropAccommodation = useCallback(
    (payload: PalettePayload, nightIdx: number) => addAccommodation(payload, nightIdx),
    [addAccommodation]
  );

  const dropService = useCallback(
    async (payload: PalettePayload, dayIdx: number, row: number) => {
      // Accommodation placeholders belong only in the accommodation strip, never on a service lane.
      if (payload.type === PlaceholderType.Accommodation) return;
      const snapshot = await resolveSnapshot(payload);
      commit((m) => {
        if (dayIdx < 0 || dayIdx >= m.lengthDays) return m;
        if (serviceCovered(m.parts, dayIdx, row)) return m;
        return {
          ...m,
          parts: [...m.parts, newSpanPart(payload, 'service', dayIdx, row, snapshot)],
        };
      });
    },
    [commit, resolveSnapshot]
  );

  const moveAccommodation = useCallback(
    (uid: string, nightIdx: number) => {
      commit((m) => {
        const moving = m.parts.find((p) => p.uid === uid);
        if (!moving || moving.kind !== 'accommodation') return m;
        for (let n = nightIdx; n < nightIdx + moving.span; n += 1) {
          if (n < 0 || n >= m.lengthDays - 1) return m;
          if (nightCovered(m.parts, n, uid)) return m;
        }
        return { ...m, parts: m.parts.map((p) => (p.uid === uid ? { ...p, day: nightIdx } : p)) };
      });
    },
    [commit]
  );

  const resizeAccommodation = useCallback(
    (uid: string, nights: number) => {
      commit((m) => ({
        ...m,
        parts: m.parts.map((p) => (p.uid === uid ? { ...p, span: Math.max(1, nights) } : p)),
      }));
    },
    [commit]
  );

  const moveService = useCallback(
    (uid: string, dayIdx: number, row: number) => {
      commit((m) => {
        const moving = m.parts.find((p) => p.uid === uid);
        if (!moving || moving.kind !== 'service') return m;
        for (let d = dayIdx; d < dayIdx + moving.span; d += 1) {
          if (d < 0 || d >= m.lengthDays) return m;
          if (serviceCovered(m.parts, d, row, uid)) return m;
        }
        return {
          ...m,
          parts: m.parts.map((p) => (p.uid === uid ? { ...p, day: dayIdx, row } : p)),
        };
      });
    },
    [commit]
  );

  const resizeService = useCallback(
    (uid: string, days: number) => {
      commit((m) => ({
        ...m,
        parts: m.parts.map((p) => (p.uid === uid ? { ...p, span: Math.max(1, days) } : p)),
      }));
    },
    [commit]
  );

  const moveTimed = useCallback(
    (uid: string, dayShift: number, hourShift: number) => {
      mapParts((part) => {
        if (part.uid !== uid || part.kind !== 'timed') return part;
        const day = Math.max(0, Math.min(modelRef.current.lengthDays - 1, part.day + dayShift));
        const rawStart = (part.startHour ?? DEFAULT_START) + hourShift;
        const startHour = Math.max(
          NIGHT_START,
          Math.min(DAY_END - part.hours, snapQuarter(rawStart))
        );
        return { ...part, day, startHour };
      });
    },
    [mapParts]
  );

  const resizeTimed = useCallback(
    (uid: string, hourDelta: number) => {
      mapParts((part) => {
        if (part.uid !== uid || part.kind !== 'timed') return part;
        const start = part.startHour ?? DEFAULT_START;
        const hours = Math.max(
          0.25,
          Math.min(DAY_END - start, snapQuarter(part.hours + hourDelta))
        );
        return { ...part, hours };
      });
    },
    [mapParts]
  );

  const updatePart = useCallback(
    (uid: string, patch: Partial<BoardPart>) => {
      mapParts((part) => {
        if (part.uid !== uid) return part;
        const next = { ...part, ...patch };
        if (next.kind === 'timed' && (patch.startHour != null || patch.hours != null)) {
          next.hours = Math.max(0.25, Math.min(DAY_END, next.hours));
          const start = next.startHour ?? DEFAULT_START;
          next.startHour = Math.max(NIGHT_START, Math.min(DAY_END - next.hours, start));
        }
        return next;
      });
    },
    [mapParts]
  );

  const deletePart = useCallback(
    (uid: string) => {
      commit((m) => ({ ...m, parts: m.parts.filter((p) => p.uid !== uid) }));
    },
    [commit]
  );

  return useMemo(
    () => ({
      model,
      dirty,
      changeToken,
      canUndo,
      undo,
      canRedo,
      redo,
      markSaved,
      dropPalette,
      dropAccommodation,
      dropService,
      moveAccommodation,
      resizeAccommodation,
      moveService,
      resizeService,
      moveTimed,
      resizeTimed,
      updatePart,
      deletePart,
    }),
    [
      model,
      dirty,
      changeToken,
      canUndo,
      undo,
      canRedo,
      redo,
      markSaved,
      dropPalette,
      dropAccommodation,
      dropService,
      moveAccommodation,
      resizeAccommodation,
      moveService,
      resizeService,
      moveTimed,
      resizeTimed,
      updatePart,
      deletePart,
    ]
  );
}
